/**
 * One renderer for every field type in lib/formSchema.js.
 *
 * The renderer is intentionally dumb: it receives a decorated field (schema merged with
 * the Firestore requirement rules), the current value, and an onChange. It never decides
 * whether a field is required or visible — see lib/fieldRules.js.
 */

import { useCallback, useRef, useState } from 'react';
import { evaluateCondition, getPath, setPath } from '../lib/conditions';
import { REQUIREMENT } from '../lib/fieldRules';
import { isPathLockedByDerivation } from '../lib/deriveMatter';
import { fieldGridClasses } from '../lib/fieldGrid';
import { formatLabelClasses, formatNoticeTextClasses, formatWrapperClasses } from '../lib/fieldFormat';
import { sortPeople } from '../lib/sortPeople';
import { personLabel } from '../lib/people';
import { useClientSearch } from '../lib/useClientSearch';
import { filterPeopleByTitles } from '../lib/peopleTitles';
import {
  clampOriginationPercent,
  effectiveOriginationCap,
  originationCapPercent,
  originationProblems,
} from '../lib/origination';
import { ADDRESS_LABELS, US_STATES, addressProblem, toAddress } from '../lib/address';
import { clampPercent, percentParams } from '../lib/fieldLayout';
import { resolveHighlight, resolveIndicator } from '../lib/requirementIndicators';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

/**
 * The disabled colours are `slate-100` / `slate-900`, and the dark one is deliberately NOT
 * slate-800: slate-800 is what an ENABLED input already has in dark mode, so a disabled — or
 * admin-locked — control measured identically to an editable one and only the cursor gave it
 * away. These live on the class string rather than in index.css's @layer base because a utility
 * beats a layered rule whatever its specificity, so the base-layer version could never win here.
 */
const input =
  'w-full rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm focus:border-firm-500 focus:outline-none focus:ring-1 focus:ring-firm-500 dark:focus:ring-firm-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:disabled:bg-slate-900 dark:disabled:text-slate-400';

const REQUIREMENT_TITLES = {
  [REQUIREMENT.REQUIRED]: 'Required',
  [REQUIREMENT.SOFT_REQUIRED]: 'Usually provided — you will be asked to explain if left blank',
};

/**
 * The marker after a field's label. Symbol and colour are admin-configurable per requirement
 * level (settings/fieldRules.indicators — see lib/requirementIndicators.js); `indicators`
 * being absent gives the historical red `*` / amber `◦`. The hover title stays fixed, so the
 * meaning of a marker is always discoverable no matter which symbol the firm picked.
 */
function RequirementBadge({ requirement, indicators }) {
  const resolved = resolveIndicator(requirement, indicators);
  if (!resolved) return null;
  return (
    <span className={`ml-1 cursor-help ${resolved.className}`} title={REQUIREMENT_TITLES[requirement]}>
      {resolved.symbol}
    </span>
  );
}

function Tags({ value = [], onChange, disabled }) {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(value) ? value : [];
  const add = () => {
    const next = draft.trim();
    if (!next) return;
    onChange([...list, next]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {list.map((tag, i) => (
          <span
            key={`${tag}-${i}`}
            className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                onClick={() => onChange(list.filter((_, idx) => idx !== i))}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <input
          className={`${input} mt-1`}
          value={draft}
          placeholder="Type and press Enter"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          onBlur={add}
        />
      )}
    </div>
  );
}

// Exported for the standalone conflict-check page. Keeping this as the one renderer for
// party rows ensures the lighter-weight flow cannot drift from the full intake form's
// Name / Type / Role / Notes interaction.
/**
 * Column-count → grid class. STATIC literals: Tailwind scans source text, so a computed
 * class name like `sm:grid-cols-${n}` would never be generated.
 */
const REPEATABLE_ROW_GRID = {
  0: 'sm:grid-cols-1',
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  // Five or six only on a wide screen; on a tablet they would each be too narrow to type in.
  5: 'sm:grid-cols-3 lg:grid-cols-5',
  6: 'sm:grid-cols-3 lg:grid-cols-6',
};

/**
 * The singular noun for one entry's card header: "Add another contact" → "Contact",
 * falling back to the group's own label ("Additional contacts" → "Additional contacts").
 * Purely cosmetic — a sensible header beats a bare numbered box.
 */
function entryNoun(field) {
  const source = field.addLabel || field.label || 'Entry';
  const stripped = String(source)
    .replace(/^\s*\+?\s*add\s+(another\s+|a\s+|an\s+)?/i, '')
    .trim();
  const noun = stripped || String(field.label || 'Entry');
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

export function Repeatable({ field, value = [], onChange, disabled, config }) {
  const given = Array.isArray(value) ? value : [];
  // `startWithOne`: an empty table shows one blank entry to type into, rather than only a small
  // "+ Add" button somebody can miss. A blank entry is not an answer (validation skips it).
  const rows = given.length === 0 && field.startWithOne === true && !disabled ? [{}] : given;
  const columns = Array.isArray(field.fields) ? field.fields : [];
  /**
   * A column's `visibleIf` is evaluated against ITS OWN ROW, not the whole form.
   *
   * "Any AALRR involvement — if yes, describe and list the attorneys" is a question about one
   * matter, and the candidate answers it differently for each. A condition read against the whole
   * matter would reveal the follow-up in every row as soon as any row said Yes.
   *
   * Paths inside a row are flat keys, which is exactly what getPath walks, so the same condition
   * grammar works unchanged (CLAUDE.md invariant 11).
   */
  const columnsFor = (row) =>
    columns.filter((sub) => sub.visibleIf === undefined || evaluateCondition(sub.visibleIf, row || {}));
  const update = (index, key, next) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: next } : row)));
  // Boxes per line: four unless the table says otherwise (the application's editor can).
  const perLine = Number.isInteger(field.maxColumns) && field.maxColumns >= 1 && field.maxColumns <= 6 ? field.maxColumns : 4;

  // An admin-created repeating group with no columns yet (or a malformed saved layout)
  // must not crash the form — say what is missing instead.
  if (columns.length === 0) {
    return (
      <p className="rounded border border-dashed border-slate-300 p-2 text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
        This repeating group has no columns yet. Add them in Admin → Field builder.
      </p>
    );
  }

  return (
    // `evenBoxes`: every box in a row the same height (see .boxes-even in index.css).
    <div className={`space-y-2 ${field.evenBoxes === true ? 'boxes-even' : ''}`}>
      {rows.map((row, index) => (
        // Each entry is a self-contained card: its own header, its own columns. Grouping
        // the row's inputs visually is what makes a repeating block readable once there
        // is more than one entry — otherwise the inputs read as one undifferentiated run.
        <div
          key={index}
          className="rounded-md border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-800/40"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {entryNoun(field)} {index + 1}
            </span>
            {!disabled && (
              <button
                type="button"
                className="text-xs text-red-600 hover:underline dark:text-red-400"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                aria-label={`Remove ${entryNoun(field).toLowerCase()} ${index + 1}`}
              >
                Remove
              </button>
            )}
          </div>
          {/*
            `stacked` puts one sub-field per row instead of squeezing them into columns.
            Beyond about four, columns stop helping: six of them wrap 4-then-2 at unequal
            heights, every textarea becomes too narrow for the prose it is asking for, and the
            second row reads as a separate group. A field asking a candidate to describe five
            years of matters is worth the vertical space.
          */}
          <div
            className={
              field.stacked === true
                ? 'space-y-2'
                : `grid gap-2 ${REPEATABLE_ROW_GRID[Math.min(columnsFor(row).length, perLine)]}`
            }
          >
            {columnsFor(row).map((sub) => (
              <label
                key={sub.path}
                // Where the table sets its boxes per line, a long answer takes a line of its own.
                className={`block text-xs text-slate-600 dark:text-slate-300 ${
                  field.maxColumns && sub.type === 'textarea' ? 'sm:col-span-full' : ''
                }`}
              >
                {sub.label}
                {sub.requirement === 'required' && <span className="text-red-600 dark:text-red-400"> *</span>}
                <Control
                  field={sub}
                  value={row[sub.path]}
                  onChange={(next) => update(index, sub.path, next)}
                  disabled={disabled}
                  config={config}
                />
              </label>
            ))}
          </div>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          className="rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500 hover:text-firm-700 dark:hover:text-firm-100"
          onClick={() => onChange([...rows, {}])}
        >
          + {field.addLabel || `Add ${field.label}`}
        </button>
      )}
    </div>
  );
}

function OriginationSplit({ field, value = [], onChange, disabled, config }) {
  const rows = Array.isArray(value) ? value : [];
  const total = rows.reduce((sum, r) => sum + (Number(r?.percent) || 0), 0);
  // The firm cap limits a SHARED split; a lone originator holds 100% (lib/origination.js).
  const firmCap = originationCapPercent(config.general);
  const capPercent = effectiveOriginationCap(firmCap, rows.length);
  // Names, so a complaint reads "Bob Associate has no origination share" rather than a raw uid.
  const nameOf = (uid) => {
    const person = (config.roster || []).find((entry) => entry.id === uid);
    return (person && (person.displayName || person.email)) || uid;
  };
  const problems = originationProblems(rows, { capPercent: firmCap, nameOf });
  const update = (index, key, next) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: next } : row)));

  // A newly added row defaults to whatever remainder is still needed to reach 100%
  // (clamped to the per-attorney cap), so a second attorney after a 75% first pick
  // starts at 25% instead of 0%. This is a one-time default at add-time only --
  // editing any row afterward never recalculates another row.
  const remainderPercent = Math.max(0, Math.min(100 - total, capPercent));

  /**
   * The people one row may choose from: the title-filtered roster minus anyone already
   * picked in ANOTHER row, so the same attorney cannot be split against themselves.
   *
   * The row's OWN current pick is always kept in its list, even when the title filter or
   * the dedup would exclude them — a `<select>` whose value is absent from its options
   * renders blank, which would look like the answer was lost and would silently write an
   * empty uid on the next change. That matters for someone picked before an admin narrowed
   * the title filter, or whose roster title changed since.
   */
  const eligible = filterPeopleByTitles(config.roster, field.titleFilter);
  const optionsFor = (index) => {
    const takenElsewhere = new Set(
      rows.filter((_, i) => i !== index).map((r) => r && r.uid).filter(Boolean)
    );
    const available = eligible.filter((u) => !takenElsewhere.has(u.id));
    const ownUid = rows[index] && rows[index].uid;
    if (ownUid && !available.some((u) => u.id === ownUid)) {
      const own = (config.roster || []).find((u) => u.id === ownUid);
      return [own || { id: ownUid, displayName: ownUid }, ...available];
    }
    return available;
  };

  return (
    <div className="space-y-2">
      {/* Shown for a single row too: a lone attorney left at 75% (the old add-time
          default) is just as wrong as a two-row split that misses 100%, and used to be
          invisible here AND to validation. */}
      {/* The problems are computed from lib/origination.js — the SAME function the submit gate
          and the server use — so this notice can never say the split is fine while validation
          disagrees, or complain about something submission would have allowed.

          Shown immediately, not held back until a failed submit: a split that adds to 120% is
          wrong the moment it is typed, and discovering it several pages later is exactly the
          late surprise worth avoiding. */}
      {rows.length > 0 &&
        (problems.length > 0 ? (
          <div
            role="alert"
            className="rounded-md border-2 border-red-500 bg-red-50 dark:border-red-600 dark:bg-red-950 p-3"
          >
            <p className="text-sm font-semibold text-red-800 dark:text-red-200">
              {problems.length === 1
                ? 'This origination split cannot be submitted'
                : `${problems.length} problems with this origination split`}
            </p>
            <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-red-800 dark:text-red-200">
              {problems.map((problem) => (
                <li key={problem.code}>{problem.message}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
            Origination total: {total}%
          </p>
        ))}
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <select
            className={input}
            value={row.uid || ''}
            disabled={disabled}
            onChange={(e) => update(index, 'uid', e.target.value)}
          >
            <option value="">{field.placeholder || 'Select attorney…'}</option>
            {/* Title-filtered (lib/peopleTitles.js — attorneys-only at most firms, but a
                firm decision rather than a hardcoded one) and de-duplicated: see
                optionsFor() above for why the row's own pick always survives both. */}
            {sortPeople(optionsFor(index)).map((u) => (
              <option key={u.id} value={u.id}>
                {personLabel(u)}
              </option>
            ))}
          </select>
          <div className="flex w-32 shrink-0 items-center gap-1">
            <input
              type="number"
              min="0"
              max={capPercent}
              className={input}
              value={row.percent ?? ''}
              disabled={disabled}
              onChange={(e) =>
                update(index, 'percent', clampOriginationPercent(e.target.value, capPercent))
              }
            />
            <span className="text-sm text-slate-500 dark:text-slate-400">%</span>
          </div>
          {!disabled && (
            <button
              type="button"
              className="text-xs text-red-600 dark:text-red-400 hover:underline"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          )}
        </div>
      ))}
      {/* Every row starts at the remainder still needed, capped at the firm maximum — so the
          first row opens at the cap rather than at 100%. Starting it at 100% would put the split
          in breach the instant it was added, which is a poor way to introduce a rule. */}
      {!disabled && (
        <button
          type="button"
          className="rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500 hover:text-firm-700 dark:hover:text-firm-100"
          onClick={() => onChange([...rows, { uid: '', percent: remainderPercent }])}
        >
          {/* Relabelable like a repeating group's Add button — this control is now often
              used for origination only, where "Add working attorney" reads wrong. */}
          + {field.addLabel || 'Add working attorney'}
        </button>
      )}
    </div>
  );
}

/**
 * Client picker, backed by the client directory the client feed imports.
 *
 * Searches by client NUMBER and by client NAME at once (lib/useClientSearch.js), because
 * people arrive with either one. The chosen client's whole imported row is stored at this
 * field's own path — `{ clientId, clientNumber, clientName, data }` — for two reasons: the
 * matter keeps a copy of what the directory said at the time it was filled in, and the
 * column-to-form-field fill in lib/deriveMatter.js can then run purely from the matter, with
 * no lookup and no round-trip. Which other form fields get filled in is admin configuration
 * (Admin → Directories → Feeds), not something this control decides.
 */
function ClientSearch({ field, value, onChange, disabled, config }) {
  const [query, setQuery] = useState('');
  // Whether inactive clients are offered at all is a firm decision (Admin -> Directories -> Feeds), not
  // this control's: opening a new matter for a client the source system has closed is a real
  // thing that happens, and it is also exactly the thing some firms want to stop.
  // Which imported directory this field picks from. Defaults to the built-in client feed; a
  // field can point at any feed an administrator has added (Field Builder → "Directory"),
  // which is what makes a new feed usable on the form without a code change.
  const feedId = field?.feedId || 'clients';
  const feedSettings = config?.feeds?.feeds?.[feedId];
  const includeInactive = feedSettings?.includeInactive === true;
  const { results, searching, error } = useClientSearch(query, { includeInactive, feedId });
  const selected = value && typeof value === 'object' ? value : null;

  if (selected && selected.clientNumber) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded bg-firm-50 dark:bg-firm-900 px-2 py-1 text-sm text-firm-700 dark:text-firm-100">
          <strong>{selected.clientNumber}</strong>
          <span>{String(selected.clientName || '').toUpperCase()}</span>
          {/* Frozen as of when the client was chosen, like the rest of the stored copy: a
              client going inactive later must not silently relabel a matter already filled in. */}
          {selected.isActive === false && (
            <span className="rounded bg-amber-100 dark:bg-amber-900 px-1 text-xs font-medium text-amber-900 dark:text-amber-100">
              inactive
            </span>
          )}
        </span>
        {!disabled && (
          <button
            type="button"
            className="text-xs text-red-600 dark:text-red-400 hover:underline"
            onClick={() => {
              setQuery('');
              onChange(null);
            }}
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        className={input}
        value={query}
        disabled={disabled}
        placeholder={field?.placeholder || 'Search by client number or name…'}
        onChange={(e) => setQuery(e.target.value)}
      />
      {/* Says plainly that this matches from the start of the number or name — the honest
          limit of a Firestore prefix query, and much better discovered here than by a person
          concluding the client is missing. */}
      {query.trim().length >= 2 && (
        <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg">
          {results.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                onClick={() => {
                  onChange({
                    feedId,
                    clientId: client.id,
                    clientNumber: client.clientNumber || '',
                    clientName: client.clientName || '',
                    isActive: client.isActive !== false,
                    data: client.data || {},
                  });
                  setQuery('');
                }}
              >
                <strong>{client.clientNumber}</strong>
                <span className="ml-2">{String(client.clientName || '').toUpperCase()}</span>
                {client.isActive === false && (
                  <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">(inactive)</span>
                )}
              </button>
            </li>
          ))}
          {!results.length && (
            <li className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400">
              {searching
                ? 'Searching…'
                : error
                  ? 'The client directory could not be searched.'
                  : includeInactive
                    ? 'No match. This searches from the start of the client number or name.'
                    : // Says which kind of matching this is, because the difference is exactly
                      // what makes a present record look absent: "463" not finding 000463 is
                      // word-start matching working as designed, not a missing client.
                      'No active client matches. This matches whole words or the start of one — "smith" finds "JOHN SMITH HOLDINGS", "mith" does not. Inactive clients are not shown.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * The four (or five) inputs of one address field.
 *
 * Laid out the way an address is written — street on its own line, then city / state / ZIP across
 * one — because that is how people read an address back to check it, and a column of five equal
 * boxes is measurably harder to scan.
 *
 * The suite line is offered unless the field turns it off, since suite and floor numbers are
 * everywhere in commercial billing addresses and an address field without one gets them jammed
 * into the street line.
 */
function AddressField({ field, value, onChange, disabled }) {
  const address = toAddress(value);
  const showSuite = field?.hideSuiteLine !== true;
  const problem = addressProblem(address, { label: field?.label || 'Address' });
  const missing = new Set(problem?.missing || []);

  // Written back as a whole object every time, so the value at this path is always a complete
  // address shape rather than accumulating keys as each box is touched.
  const set = (part, next) => onChange({ ...address, [part]: next });

  const partClass = (part) =>
    `${input} ${missing.has(part) ? 'border-red-400 dark:border-red-600' : ''}`;

  const label = (part, text) => (
    <span className="mb-0.5 block text-xs text-slate-500 dark:text-slate-400">{text}</span>
  );

  return (
    <div className="space-y-2">
      <label className="block">
        {label('street', ADDRESS_LABELS.street)}
        <input
          className={partClass('street')}
          value={address.street}
          disabled={disabled}
          autoComplete="address-line1"
          onChange={(e) => set('street', e.target.value)}
          aria-label={`${field?.label || 'Address'} — street`}
        />
      </label>

      {showSuite && (
        <label className="block">
          {label('street2', ADDRESS_LABELS.street2)}
          <input
            className={input}
            value={address.street2}
            disabled={disabled}
            autoComplete="address-line2"
            onChange={(e) => set('street2', e.target.value)}
            aria-label={`${field?.label || 'Address'} — suite or floor`}
          />
        </label>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[2fr_1fr_1fr]">
        <label className="block">
          {label('city', ADDRESS_LABELS.city)}
          <input
            className={partClass('city')}
            value={address.city}
            disabled={disabled}
            autoComplete="address-level2"
            onChange={(e) => set('city', e.target.value)}
            aria-label={`${field?.label || 'Address'} — city`}
          />
        </label>
        <label className="block">
          {label('state', ADDRESS_LABELS.state)}
          {/* A select rather than free text: it is what makes the stored value comparable, which
              is what a future feed match depends on. "ca", "Ca", "Calif." and "California" are
              four different strings for one state. */}
          <select
            className={partClass('state')}
            value={address.state}
            disabled={disabled}
            autoComplete="address-level1"
            onChange={(e) => set('state', e.target.value)}
            aria-label={`${field?.label || 'Address'} — state`}
          >
            <option value="">—</option>
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code} title={name}>
                {code}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          {label('zip', ADDRESS_LABELS.zip)}
          <input
            className={partClass('zip')}
            value={address.zip}
            disabled={disabled}
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="90071"
            onChange={(e) => set('zip', e.target.value)}
            aria-label={`${field?.label || 'Address'} — ZIP`}
          />
        </label>
      </div>

      {/* Shown as soon as the address is part-filled, not held back until a failed submit: a
          half-typed address is the state worth catching, and it is invisible to a plain
          "is this blank" check because something IS in there. */}
      {problem && (
        <p className="text-xs text-red-700 dark:text-red-300">{problem.message}</p>
      )}
    </div>
  );
}

function AreaOfLawSelect({ value, onChange, disabled, config }) {
  const areas = config.areasOfLaw?.items || [];
  // Pre-dropdown matters retain their historical array shape. The editor cannot
  // represent multiple values in the new single-select control, so show the first
  // saved choice until the user deliberately saves a new scalar value. All display
  // paths remain fully array-tolerant for old submitted matters.
  const selected = Array.isArray(value) ? value[0] || '' : value || '';

  return (
    <select
      className={input}
      disabled={disabled}
      value={selected}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Select area of law…</option>
      {areas
        .filter((a) => a.isActive !== false)
        .map((area) => (
          <option key={area.id} value={area.id}>
            {area.label}
          </option>
        ))}
    </select>
  );
}

/**
 * Matter Type (Issue 4): a single-select control backed by two legacy boolean paths
 * (matterType.isLitigation / matterType.isNonLitigation) that approval-flow conditions
 * elsewhere already read independently — keeping both in sync here means nothing
 * downstream needs to change shape, while the UI itself only ever allows one at a time.
 */
function MatterTypeSelect({ field, matter, onChange, disabled }) {
  const isLit = getPath(matter, 'matterType.isLitigation') === true;
  const isNonLit = getPath(matter, 'matterType.isNonLitigation') === true;
  const current = isLit ? 'litigation' : isNonLit ? 'non_litigation' : '';

  const choose = (val) => {
    let next = setPath(matter, 'matterType.isLitigation', val === 'litigation');
    next = setPath(next, 'matterType.isNonLitigation', val === 'non_litigation');
    // Also write the selection string itself, not just the two legacy booleans — the
    // rules engine, the PDF renderer, and the required-field indicator all key off
    // matterType.selection, so leaving it blank here is what caused the bug where a
    // radio choice never cleared the "Matter Type is required" error / red ring.
    next = setPath(next, 'matterType.selection', val || '');
    onChange(next);
  };

  return (
    <div className="space-y-1">
      {(Array.isArray(field.options) ? field.options : []).map((opt) => (
        <label key={opt.value} className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={field.path}
            disabled={disabled}
            checked={current === opt.value}
            onChange={() => choose(opt.value)}
          />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

/**
 * Reusable chip-style practice-group multi-select. Both Header & Billing's
 * additional groups and Wall Builder use this exact control, while retaining
 * separate field values. It follows PersonSearch's chip/remove treatment but
 * keeps every unselected practice group visibly available (there are few enough
 * groups that a search box would add noise).
 */
function PracticeGroupMultiSelect({ value = [], onChange, disabled, config, excludeIds = [] }) {
  const [open, setOpen] = useState(false);
  // Clicking away closes the panel. The ref goes on the root so the button that opens it counts
  // as inside — on the panel alone, the same press would open and immediately close it.
  const rootRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissOnOutside(rootRef, open, close);
  const groups = (config.practiceGroups || []).filter((g) => g.isActive !== false);
  const selectedIds = Array.isArray(value) ? value : [];
  const excluded = new Set(excludeIds.filter(Boolean));
  const selectedGroups = selectedIds
    .map((id) => groups.find((g) => g.id === id))
    .filter(Boolean);
  const availableGroups = groups.filter((g) => !selectedIds.includes(g.id) && !excluded.has(g.id));

  const add = (id) => {
    onChange([...selectedIds, id]);
    setOpen(false);
  };
  const remove = (id) => onChange(selectedIds.filter((selectedId) => selectedId !== id));

  return (
    <div ref={rootRef}>
      {selectedGroups.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {selectedGroups.map((group) => (
            <span
              key={group.id}
              className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
            >
              {group.name}{group.code ? ` (${group.code})` : ''}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${group.name}`}
                  className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => remove(group.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && !open && availableGroups.length > 0 && (
        <button
          type="button"
          className="rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500 hover:text-firm-700 dark:hover:text-firm-100"
          onClick={() => setOpen(true)}
        >
          + Add practice group
        </button>
      )}
      {!disabled && open && (
        <div className="relative">
          <ul
            aria-label="Available practice groups"
            className="overflow-hidden rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg"
          >
            {availableGroups.map((group) => (
              <li key={group.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                  onClick={() => add(group.id)}
                >
                  {group.name}{group.code ? ` (${group.code})` : ''}
                </button>
              </li>
            ))}
            {availableGroups.length === 0 && (
              <li className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400">
                No additional practice groups available.
              </li>
            )}
          </ul>
          <button
            type="button"
            className="mt-1 text-xs text-slate-500 dark:text-slate-400 hover:underline"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      )}
      {groups.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">No practice groups configured.</p>
      )}
    </div>
  );
}

/**
 * Reusable roster-backed chip picker for administrator-created People (multiple) fields.
 * Keep the source list as its own variable so a future roster role/type filter can refine
 * it without changing the selection, chip, or dropdown interaction.
 */
export function PeopleMultiSelect({ field, value = [], onChange, disabled, config }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  // Clicking away closes the panel and clears the search, exactly as the Close link did. On a
  // roster of several hundred people that link sits below the fold, so the panel read as stuck.
  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useDismissOnOutside(rootRef, open, close);
  const people = config.roster || [];
  const selectedIds = Array.isArray(value) ? value : [];
  const selectedPeople = selectedIds
    .map((id) => people.find((person) => person.id === id))
    .filter(Boolean);
  const availablePeople = sortPeople(
    filterPeopleByTitles(people, field.titleFilter).filter((person) => !selectedIds.includes(person.id))
  );
  const q = query.trim().toLowerCase();
  const filteredAvailablePeople =
    q.length === 0
      ? availablePeople
      : availablePeople.filter(
          (person) =>
            (person.displayName || '').toLowerCase().includes(q) ||
            (person.email || '').toLowerCase().includes(q) ||
            (person.employeeCode || '').toLowerCase().includes(q)
        );

  const add = (id) => {
    onChange([...selectedIds, id]);
    setQuery('');
    setOpen(false);
  };
  const remove = (id) => onChange(selectedIds.filter((selectedId) => selectedId !== id));

  return (
    <div ref={rootRef}>
      {selectedPeople.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {selectedPeople.map((person) => (
            <span
              key={person.id}
              className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
            >
              {personLabel(person)}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${personLabel(person)}`}
                  className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => remove(person.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && !open && availablePeople.length > 0 && (
        <button
          type="button"
          className="rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500 hover:text-firm-700 dark:hover:text-firm-100"
          onClick={() => setOpen(true)}
        >
          + Add person
        </button>
      )}
      {!disabled && open && (
        <div className="relative">
          <input
            className={input}
            value={query}
            placeholder={field?.placeholder || 'Search people by name or email…'}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul
            aria-label="Available people"
            className="mt-1 max-h-56 overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg"
          >
            {filteredAvailablePeople.map((person) => (
              <li key={person.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                  onClick={() => add(person.id)}
                >
                  {personLabel(person)}
                </button>
              </li>
            ))}
            {filteredAvailablePeople.length === 0 && (
              <li className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400">
                {availablePeople.length === 0 ? 'No additional people available.' : 'No matches.'}
              </li>
            )}
          </ul>
          <button
            type="button"
            className="mt-1 text-xs text-slate-500 dark:text-slate-400 hover:underline"
            onClick={() => {
              setOpen(false);
              setQuery('');
            }}
          >
            Close
          </button>
        </div>
      )}
      {people.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">No people available.</p>
      )}
    </div>
  );
}

/**
 * Reusable chip multi-select backed by an administrator-defined option list (field.options),
 * for custom fields where the choices are curated by an admin in Field Builder rather than
 * pulled from the roster or practice-group config. Mirrors PracticeGroupMultiSelect's
 * selection/chip/dropdown interaction exactly, keyed by option.value/option.label instead
 * of a config-backed id list.
 */
function OptionsMultiSelect({ value = [], onChange, disabled, field }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismissOnOutside(rootRef, open, close);
  const options = Array.isArray(field?.options) ? field.options : [];
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedOptions = selectedValues
    .map((v) => options.find((option) => option.value === v))
    .filter(Boolean);
  const availableOptions = options.filter((option) => !selectedValues.includes(option.value));

  const add = (v) => {
    onChange([...selectedValues, v]);
    setOpen(false);
  };
  const remove = (v) => onChange(selectedValues.filter((selectedValue) => selectedValue !== v));

  return (
    <div ref={rootRef}>
      {selectedOptions.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
            >
              {option.label}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => remove(option.value)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && !open && availableOptions.length > 0 && (
        <button
          type="button"
          className="rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500 hover:text-firm-700 dark:hover:text-firm-100"
          onClick={() => setOpen(true)}
        >
          + Add
        </button>
      )}
      {!disabled && open && (
        <div className="relative">
          <ul
            aria-label="Available options"
            className="overflow-hidden rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg"
          >
            {availableOptions.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                  onClick={() => add(option.value)}
                >
                  {option.label}
                </button>
              </li>
            ))}
            {availableOptions.length === 0 && (
              <li className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400">
                No additional options available.
              </li>
            )}
          </ul>
          <button
            type="button"
            className="mt-1 text-xs text-slate-500 dark:text-slate-400 hover:underline"
            onClick={() => setOpen(false)}
          >
            Close
          </button>
        </div>
      )}
      {options.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">No options configured for this field.</p>
      )}
    </div>
  );
}

/**
 * Wall Builder roster search (Issue 3): a lightweight typeahead over `config.roster`,
 * for designating specific individuals (as opposed to whole practice groups) permitted
 * access. No autocomplete/combobox component existed anywhere in this codebase before
 * this change — the "Responsible Partner picker" the spec pointed to for this pattern
 * is actually just a plain `<select>` (see the userSelect case below), not a search —
 * so this is a new, small, purpose-built component rather than a reuse of an existing
 * one. See TASK_SPEC / final report for that interpretation note.
 */
function PersonSearch({ field, value = [], onChange, disabled, config }) {
  const [query, setQuery] = useState('');
  const roster = config.roster || [];
  const selectedIds = Array.isArray(value) ? value : [];
  const selectedPeople = selectedIds
    .map((id) => roster.find((u) => u.id === id))
    .filter(Boolean);

  const q = query.trim().toLowerCase();
  const matches =
    q.length === 0
      ? []
      : sortPeople(
          filterPeopleByTitles(roster, field.titleFilter)
            .filter((u) => !selectedIds.includes(u.id))
            .filter(
              (u) =>
                (u.displayName || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q) ||
                (u.employeeCode || '').toLowerCase().includes(q)
            )
        ).slice(0, 8);

  const add = (uid) => {
    onChange([...selectedIds, uid]);
    setQuery('');
  };
  const remove = (uid) => onChange(selectedIds.filter((id) => id !== uid));

  return (
    <div>
      {selectedPeople.length > 0 && (
        <div className="mb-1 flex flex-wrap gap-1">
          {selectedPeople.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1 rounded bg-firm-50 dark:bg-firm-900 px-2 py-0.5 text-xs text-firm-700 dark:text-firm-100"
            >
              {personLabel(u)}
              {!disabled && (
                <button
                  type="button"
                  className="text-firm-500 dark:text-firm-100 hover:text-red-600 dark:hover:text-red-400"
                  onClick={() => remove(u.id)}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="relative">
          <input
            className={input}
            value={query}
            placeholder={field?.placeholder || 'Search people by name or email…'}
            onChange={(e) => setQuery(e.target.value)}
          />
          {matches.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm shadow-lg">
              {matches.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-1.5 text-left hover:bg-firm-50 dark:hover:bg-firm-900"
                    onClick={() => add(u.id)}
                  >
                    {personLabel(u)}
                    <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
                      {u.email}
                      {u.employeeCode ? ` · ${u.employeeCode}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Control({ field, value, onChange, disabled, config, matter, inputId }) {
  const common = { className: input, disabled, id: field.path };

  switch (field.type) {
    case 'matterTypeSelect':
      // onChange here receives a full replacement matter, not a field value — see
      // MatterTypeSelect above and its call site in Field below.
      return (
        <MatterTypeSelect field={field} matter={matter} onChange={onChange} disabled={disabled} />
      );

    case 'textarea':
      return (
        <textarea
          {...common}
          rows={field.rows || 3}
          value={value ?? ''}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'number':
    case 'money':
      // The $ sits INSIDE the field, at the same baseline as the value. Beside the box it read
      // as a separate label and left the input misaligned with every other control on the row.
      return (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-[15px] text-slate-400"
          >
            $
          </span>
          <input
            {...common}
            type="number"
            step="0.01"
            placeholder="0.00"
            className={`${input} pl-[30px]`}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      );

    case 'percent': {
      // min/max/decimals are admin-set per field (Field Builder → Percentage range).
      //
      // The min/max attributes alone do NOT stop anyone typing out of range: on a number input
      // they bind the stepper arrows and a native form submit, and this app never does a native
      // submit. So the value is clamped on blur as well, and fieldRules.js still re-checks the
      // range at validation time for anything that reaches it another way (a feed, a template,
      // a draft saved before the range was narrowed).
      //
      // On blur rather than on each keystroke, deliberately: in a 10–100 field, clamping while
      // typing would snap the "5" of "50" up to 10 the moment it was pressed, and the next
      // keypress would make 100 — putting the value the user wanted out of reach entirely.
      const { min, max, decimals } = percentParams(field);
      return (
        <div className="flex items-center gap-1">
          <input
            {...common}
            type="number"
            min={min}
            max={max}
            step={decimals > 0 ? 1 / 10 ** decimals : 1}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            onBlur={(e) => {
              const clamped = clampPercent(e.target.value, field);
              if (clamped !== (value ?? null)) onChange(clamped);
            }}
          />
          <span className="text-slate-500 dark:text-slate-400">%</span>
        </div>
      );
    }

    case 'date':
    case 'email':
    case 'tel':
      return (
        <input
          {...common}
          type={field.type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'yesno':
      // Pill labels rather than bare radios: a 46px target that matches the height of every
      // other control on the row, so a Yes/No sitting beside a select does not read as a
      // different kind of thing. The whole pill is the hit area — a 13px radio dot is a poor
      // target and an unclear one when the label is what people aim at.
      return (
        <div className="flex items-center gap-3">
          {[
            [field.yesLabel || 'Yes', true],
            [field.noLabel || 'No', false],
          ].map(([label, val]) => {
            const selected = value === val;
            return (
              <label
                key={label}
                className={`flex h-[46px] flex-1 cursor-pointer items-center gap-2.5 rounded-md border px-4 text-[15px] transition-colors ${
                  selected
                    ? 'border-[1.5px] border-accent-500 bg-accent-50 font-medium text-firm-700 dark:bg-firm-900 dark:text-firm-100'
                    : 'border-slate-200 text-ink hover:border-slate-300 dark:border-slate-600 dark:text-slate-300'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name={field.path}
                  className="sr-only"
                  disabled={disabled}
                  checked={selected}
                  onChange={() => onChange(val)}
                />
                {/* Drawn rather than native, so the selected ring is the accent at a usable
                    size; the real input stays in the DOM for keyboard and screen readers. */}
                <span
                  aria-hidden="true"
                  className={`h-[17px] w-[17px] shrink-0 rounded-full ${
                    selected
                      ? 'border-[5px] border-accent-500 bg-white'
                      : 'border-[1.5px] border-slate-300 dark:border-slate-500'
                  }`}
                />
                {label}
              </label>
            );
          })}
          {value !== undefined && value !== null && !disabled && (
            <button
              type="button"
              className="shrink-0 text-xs text-slate-500 hover:text-firm-700 dark:hover:text-slate-200"
              onClick={() => onChange(null)}
            >
              clear
            </button>
          )}
        </div>
      );

    case 'money':
      return (
        <div className="flex items-center gap-1">
          {field.type === 'money' && <span className="text-slate-500 dark:text-slate-400">$</span>}
          <input
            {...common}
            type="number"
            step={field.type === 'money' ? '0.01' : '1'}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      );

    case 'percent': {
      // min/max/decimals are admin-set per field (Field Builder → Percentage range).
      //
      // The min/max attributes alone do NOT stop anyone typing out of range: on a number input
      // they bind the stepper arrows and a native form submit, and this app never does a native
      // submit. So the value is clamped on blur as well, and fieldRules.js still re-checks the
      // range at validation time for anything that reaches it another way (a feed, a template,
      // a draft saved before the range was narrowed).
      //
      // On blur rather than on each keystroke, deliberately: in a 10–100 field, clamping while
      // typing would snap the "5" of "50" up to 10 the moment it was pressed, and the next
      // keypress would make 100 — putting the value the user wanted out of reach entirely.
      const { min, max, decimals } = percentParams(field);
      return (
        <div className="flex items-center gap-1">
          <input
            {...common}
            type="number"
            min={min}
            max={max}
            step={decimals > 0 ? 1 / 10 ** decimals : 1}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            onBlur={(e) => {
              const clamped = clampPercent(e.target.value, field);
              if (clamped !== (value ?? null)) onChange(clamped);
            }}
          />
          <span className="text-slate-500 dark:text-slate-400">%</span>
        </div>
      );
    }

    case 'date':
    case 'email':
    case 'tel':
      return (
        <input
          {...common}
          type={field.type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case 'yesno':
      return (
        <div className="flex gap-4">
          {[
            [field.yesLabel || 'Yes', true],
            [field.noLabel || 'No', false],
          ].map(([label, val]) => (
            <label key={label} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name={field.path}
                disabled={disabled}
                checked={value === val}
                onChange={() => onChange(val)}
              />
              {label}
            </label>
          ))}
          {value !== undefined && value !== null && !disabled && (
            <button
              type="button"
              className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={() => onChange(null)}
            >
              clear
            </button>
          )}
        </div>
      );

    case 'checkbox':
      return (
        // Sized up from the browser default: the candidate form's certification is a paragraph
        // of legal language above a single box, and a 13px default tick under 60 words of it is
        // not a control anybody sees.
        //
        // `inputId` ties the label above to this box, so clicking the certification's wording ticks
        // it — without that, only the 16px box itself responded. Opt-in rather than always
        // `field.path`, because table cells render checkboxes with the same column path in every
        // row and duplicate ids would point every label at the first row.
        <input
          id={inputId}
          type="checkbox"
          className="mt-1 h-4 w-4 accent-firm-700 dark:accent-firm-400"
          disabled={disabled}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
      );

    case 'radio':
      return (
        <div className="space-y-1">
          {(Array.isArray(field.options) ? field.options : []).map((opt) => (
            <label key={opt.value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                className="mt-0.5"
                name={field.path}
                disabled={disabled}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <span>
                {opt.label}
                {opt.note && <em className="block text-xs text-amber-700 dark:text-amber-300">{opt.note}</em>}
              </span>
            </label>
          ))}
        </div>
      );

    case 'select':
      // `display: 'list'`: every choice shown, its wording wrapping, for choices too long to read in
      // a dropdown (the race categories are a paragraph each). Same stored answer as the dropdown,
      // and it can be cleared, since a radio button cannot be un-picked.
      if (field.display === 'list') {
        return (
          <div className="space-y-1.5">
            {(Array.isArray(field.options) ? field.options : []).map((opt) => (
              <label key={opt.value} className="flex items-start gap-2 text-sm leading-snug">
                <input
                  type="radio"
                  className="mt-0.5 shrink-0"
                  name={field.path}
                  disabled={disabled}
                  checked={value === opt.value}
                  onChange={() => onChange(opt.value)}
                />
                <span className="min-w-0 break-words">{opt.label}</span>
              </label>
            ))}
            {!disabled && value !== undefined && value !== null && value !== '' && (
              <button
                type="button"
                className="text-xs text-firm-700 underline dark:text-firm-300"
                onClick={() => onChange('')}
              >
                {field.clearLabel || 'Clear my answer'}
              </button>
            )}
          </div>
        );
      }
      return (
        <select {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">{field.placeholder || 'Select…'}</option>
          {(Array.isArray(field.options) ? field.options : []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'multicheck': {
      const options =
        field.options ||
        (field.optionsFrom === 'fileSubtypes'
          ? (config.general?.fileSubtypes || []).map((s) => ({ value: s.id, label: s.label }))
          : []);
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((opt) => (
            <label key={opt.value} className="flex min-w-0 items-start gap-2 text-sm leading-5">
              <input
                type="checkbox"
                className="mt-0.5 shrink-0"
                disabled={disabled}
                checked={selected.includes(opt.value)}
                onChange={() =>
                  onChange(
                    selected.includes(opt.value)
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value]
                  )
                }
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }

    case 'tags':
      return <Tags value={value} onChange={onChange} disabled={disabled} />;

    case 'repeatable':
      return (
        <Repeatable
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          config={config}
        />
      );

    case 'originationSplit':
      return (
        <OriginationSplit field={field} value={value} onChange={onChange} disabled={disabled} config={config} />
      );

    case 'areaOfLawSelect':
      return <AreaOfLawSelect value={value} onChange={onChange} disabled={disabled} config={config} />;

    case 'userSelect':
      return (
        <select {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">{field.placeholder || 'Select…'}</option>
          {sortPeople(filterPeopleByTitles(config.roster, field.titleFilter)).map((u) => (
            <option key={u.id} value={u.id}>
              {personLabel(u)}
            </option>
          ))}
        </select>
      );

    case 'practiceGroupSelect':
      return (
        <select {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select practice group…</option>
          {config.practiceGroups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      );

    case 'practiceGroupMultiSelect':
      return (
        <PracticeGroupMultiSelect
          value={value}
          onChange={onChange}
          disabled={disabled}
          config={config}
          excludeIds={
            field.path === 'header.additionalPracticeGroupIds'
              ? [getPath(matter, 'header.practiceGroupId')]
              : []
          }
        />
      );

    case 'peopleMultiSelect':
      return <PeopleMultiSelect field={field} value={value} onChange={onChange} disabled={disabled} config={config} />;

    case 'optionsMultiSelect':
      return <OptionsMultiSelect value={value} onChange={onChange} disabled={disabled} field={field} />;

    case 'personSearch':
      return <PersonSearch field={field} value={value} onChange={onChange} disabled={disabled} config={config} />;

    case 'address':
      return <AddressField field={field} value={value} onChange={onChange} disabled={disabled} />;

    case 'clientSearch':
      return (
        <ClientSearch field={field} value={value} onChange={onChange} disabled={disabled} config={config} />
      );

    case 'attachments':
      return field.render ? field.render() : null;

    default:
      return (
        <input
          {...common}
          type="text"
          value={value ?? ''}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

export default function Field({
  field,
  matter,
  onChange,
  disabled: disabledProp,
  config,
  problem,
  liveIncomplete,
  inputId,
}) {
  /**
   * A disabled field is shown and greyed out, not hidden. The answer still belongs on the form
   * and still prints; the person filling it in simply is not the one who sets it.
   *
   * Three ways it can be disabled, ORed so the strictest wins:
   *   - the caller says so (a read-only view of a submitted matter), which no field-level
   *     setting may override;
   *   - it is always disabled (`isDisabled`, from settings/fieldRules or a layout override);
   *   - its condition matches right now (`disabledIf`).
   *
   * The `!== undefined` guard on disabledIf is load-bearing, not defensive noise:
   * evaluateCondition(undefined) returns TRUE by design, because an absent `visibleIf` has to
   * mean "always visible". Reusing it here without the guard would mean an absent `disabledIf`
   * read as "always disabled" — every field in the app, greyed out.
   *
   * A plain const, not a hook, and deliberately above the early returns below: this component
   * returns from several branches, and anything hook-shaped here would change hook order between
   * them. See hookOrder.test.js.
   */
  const disabled =
    disabledProp ||
    field.isDisabled === true ||
    (field.disabledIf !== undefined && evaluateCondition(field.disabledIf, matter)) ||
    // An auto-fill rule set to "do not allow this to be changed" holds its target field. Asked of
    // the same rule objects that write the value, so a field can never be greyed out by a rule
    // that is not setting it, nor editable while a rule silently overwrites what is typed.
    isPathLockedByDerivation(field.path, matter, config);

  if (field.type === 'notice') {
    // 'plain' renders as bare text — no border, background, or box padding — for
    // notices that should read as part of the page rather than as a banner.
    // Size / bold / italic / underline come from the notice's own format object; the
    // colour scheme comes from its variant. whitespace-pre-line keeps deliberate line
    // breaks an admin typed into the message.
    const noticeText = `${formatNoticeTextClasses(field)} whitespace-pre-line`;
    if (field.variant === 'plain') {
      return (
        <div className={`${noticeText} text-slate-700 dark:text-slate-200 ${fieldGridClasses(field)}`}>
          {field.text}
        </div>
      );
    }
    // A warning is a rule down the left and a glyph, not a boxed banner: it reads as an aside
    // attached to the page rather than as a second competing surface, and sentence case keeps it
    // from shouting.
    if (field.variant === 'warning') {
      return (
        <div
          className={`flex gap-3 rounded-r-md border-l-[3px] border-amber-500 bg-amber-50 px-5 py-4 ${noticeText} text-amber-900 dark:bg-amber-950 dark:text-amber-100 ${fieldGridClasses(field)}`}
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="mt-0.5 h-[18px] w-[18px] shrink-0 text-amber-700 dark:text-amber-300"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5.5M12 16.2v.3" />
          </svg>
          <span className="leading-[1.6]">{field.text}</span>
        </div>
      );
    }
    return (
      <div
        className={`rounded-md border border-slate-200 bg-white p-4 ${noticeText} text-ink dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 ${fieldGridClasses(field)}`}
      >
        {field.text}
      </div>
    );
  }

  const value = getPath(matter, field.path);
  // matterTypeSelect writes to two sibling boolean paths at once, so it needs the raw
  // top-level onChange (a full replacement matter) instead of the single-path `handle`.
  const handle =
    field.type === 'matterTypeSelect' ? onChange : (next) => onChange(setPath(matter, field.path, next));
  const span = fieldGridClasses(field);
  // A field carrying `derivedFrom` (e.g. origination.responsiblePartnerUid mirroring
  // header.responsiblePartnerUid — see deriveMatter.js's applyResponsiblePartnerMirror) is
  // never independently editable, regardless of whatever disabled state the page already
  // passed down for other reasons (e.g. a read-only submitted matter) — this is an OR, not
  // a replacement, so that existing disabled logic keeps working everywhere else.
  const effectiveDisabled = disabled || Boolean(field.derivedFrom);

  // Admin-chosen emphasis (bold label, background tint, border box — lib/fieldFormat.js).
  // An unformatted field gets empty extras, so it renders exactly as it always has.
  const formatExtras = formatWrapperClasses(field);
  // Admin-configured outline around the control. Off unless a firm switches it on, and its
  // trigger defaults to "only after a failed submit" — see requirementIndicators.js.
  const highlightClass = resolveHighlight(field.requirement, config?.fieldRules?.indicators, {
    blank: Boolean(liveIncomplete),
    invalid: Boolean(problem),
  });

  return (
    <div className={formatExtras ? `${span} ${formatExtras}` : span}>
      <label htmlFor={field.path} className={formatLabelClasses(field)}>
        {field.label}
        <RequirementBadge requirement={field.requirement} indicators={config?.fieldRules?.indicators} />
      </label>
      {/*
        The outline is opt-in per requirement level and defaults to firing only after a failed
        submit. An untouched required field is the normal state of a form someone just opened,
        so outlining every one of them on arrival — which is what the old hardcoded red ring
        did — reads as a page full of errors rather than a page waiting to be filled in.
      */}
      <div className={`mt-1 ${highlightClass}`.trimEnd()}>
        <Control
          field={field}
          value={value}
          onChange={handle}
          disabled={effectiveDisabled}
          config={config}
          matter={matter}
          inputId={inputId}
        />
      </div>
      {field.help && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{field.help}</p>}
      {field.maxLength && typeof value === 'string' && (
        <p className="mt-0.5 text-right text-xs text-slate-400 dark:text-slate-500">
          {value.length}/{field.maxLength}
        </p>
      )}
      {problem && (
        <p
          className={`mt-1 text-xs ${
            problem.level === REQUIREMENT.REQUIRED ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-300'
          }`}
        >
          {problem.message}
        </p>
      )}
    </div>
  );
}
